import { atom } from "jotai";
import { AuthHandler } from "../handlers/auth-handler";
import {
  // secureStorage,
  createSecureStorage,
  SecureStorageKeys,
} from "@pineseed/gui/util/secure-storage";
import type {
  CasdoorUser,
  AuthTokens,
  AuthState,
  AuthConfig,
} from "@pineseed/gui/types/auth";
import type { PSAuthConfig } from "../handlers/auth-handler";
// import type { BrandConfig } from "../config/brand";

const secureStorage = createSecureStorage();

/**
 * Auth State Management - 100% Generated API Based
 * STRICT RULE: Only atoms, no business logic, no UI code, no external calls
 * All business logic handled by auth-handler.ts
 */

// ===== CORE AUTH STATE =====

export const authStateAtom = atom<AuthState>({
  isAuthenticated: false,
  user: null,
  tokens: null,
  isLoading: false,
  error: null,
  loginUrl: null,
  isInitialized: false,
});

// ===== AUTH CONFIGURATION =====

// Brand config atom - stores the brand configuration passed from app level
const _pineSeedAuthAtom = atom<PSAuthConfig | null>(null);

export const pineSeedAuthAtom = atom(
  (get) => get(_pineSeedAuthAtom),
  (_get, set, newValue: PSAuthConfig | null) => {
    set(_pineSeedAuthAtom, newValue);
  },
);

// Internal atom for auth config
const _authConfigAtom = atom<AuthConfig>({
  casdoorEndpoint: "",
  clientId: "",
  appName: "",
  organizationName: "",
  redirectUri: "",
  scope: "openid profile email",
});

// Auth config atom - SECURE STORAGE (contains sensitive client IDs)
export const authConfigAtom = atom(
  (get) => get(_authConfigAtom),
  async (_get, set, newValue: AuthConfig) => {
    await secureStorage.setItem(
      SecureStorageKeys.AUTH_CONFIG,
      JSON.stringify(newValue),
    );
    set(_authConfigAtom, newValue);
  },
);

// ===== PERSISTENT TOKENS =====

// Internal atoms for in-memory storage
const _accessTokenAtom = atom<string | null>(null);
const _refreshTokenAtom = atom<string | null>(null);

// Store access token separately for quick access - SECURE STORAGE
export const accessTokenAtom = atom(
  (get) => get(_accessTokenAtom),
  async (_get, set, newValue: string | null) => {
    if (newValue) {
      await secureStorage.setItem(SecureStorageKeys.ACCESS_TOKEN, newValue);
    } else {
      await secureStorage.removeItem(SecureStorageKeys.ACCESS_TOKEN);
    }
    set(_accessTokenAtom, newValue);
  },
);

// Store refresh token separately for token refresh - SECURE STORAGE
export const refreshTokenAtom = atom(
  (get) => get(_refreshTokenAtom),
  async (_get, set, newValue: string | null) => {
    if (newValue) {
      await secureStorage.setItem(SecureStorageKeys.REFRESH_TOKEN, newValue);
    } else {
      await secureStorage.removeItem(SecureStorageKeys.REFRESH_TOKEN);
    }
    set(_refreshTokenAtom, newValue);
  },
);

// ===== SECURE STORAGE LOADERS =====

// Load access token from secure storage on app start
export const loadAccessTokenAtom = atom(null, async (_get, set) => {
  try {
    const token = await secureStorage.getItem(SecureStorageKeys.ACCESS_TOKEN);
    set(_accessTokenAtom, token);
  } catch (error) {
    console.warn("Failed to load access token from secure storage:", error);
  }
});

// Load refresh token from secure storage on app start
export const loadRefreshTokenAtom = atom(null, async (_get, set) => {
  try {
    const token = await secureStorage.getItem(SecureStorageKeys.REFRESH_TOKEN);
    set(_refreshTokenAtom, token);
  } catch (error) {
    console.warn("Failed to load refresh token from secure storage:", error);
  }
});

// Load auth config from secure storage on app start
export const loadAuthConfigAtom = atom(null, async (_get, set) => {
  try {
    const stored = await secureStorage.getItem(SecureStorageKeys.AUTH_CONFIG);
    if (stored) {
      const config = JSON.parse(stored);
      set(_authConfigAtom, config);
    }
  } catch (error) {
    console.warn("Failed to load auth config from secure storage:", error);
  }
});

// Load auth UI mode from secure storage on app start
export const loadAuthUIModeAtom = atom(null, async (_get, set) => {
  try {
    const mode = await secureStorage.getItem(SecureStorageKeys.AUTH_UI_MODE);
    if (mode) {
      set(_authUIModeAtom, mode);
    }
  } catch (error) {
    console.warn("Failed to load auth UI mode from secure storage:", error);
  }
});

// ===== DERIVED STATE =====

export const isAuthenticatedAtom = atom(
  (get) => get(authStateAtom).isAuthenticated,
);

export const currentUserAtom = atom((get) => get(authStateAtom).user);

export const authTokensAtom = atom((get) => get(authStateAtom).tokens);

export const authLoadingAtom = atom((get) => get(authStateAtom).isLoading);

export const authErrorAtom = atom((get) => get(authStateAtom).error);

export const loginUrlAtom = atom((get) => get(authStateAtom).loginUrl);

export const isInitializedAtom = atom(
  (get) => get(authStateAtom).isInitialized,
);

export const currentUserEmailAtom = atom((get) => {
  const user = get(currentUserAtom);
  return user?.id || null;
});

// ===== AUTH STATE ACTIONS =====

export const setAuthStateAtom = atom(
  null,
  (get, set, updates: Partial<AuthState>) => {
    const currentState = get(authStateAtom);
    set(authStateAtom, {
      ...currentState,
      ...updates,
    });
  },
);

export const setLoadingAtom = atom(null, (get, set, loading: boolean) => {
  const currentState = get(authStateAtom);
  set(authStateAtom, {
    ...currentState,
    isLoading: loading,
  });
});

export const setErrorAtom = atom(null, (get, set, error: string | null) => {
  const currentState = get(authStateAtom);
  set(authStateAtom, {
    ...currentState,
    error,
  });
});

export const setLoginUrlAtom = atom(
  null,
  (get, set, loginUrl: string | null) => {
    const currentState = get(authStateAtom);
    set(authStateAtom, {
      ...currentState,
      loginUrl,
    });
  },
);

export const setAuthenticatedAtom = atom(
  null,
  async (
    get,
    set,
    isAuthenticated: boolean,
    user: CasdoorUser | null,
    tokens: AuthTokens | null,
  ) => {
    const currentState = get(authStateAtom);
    set(authStateAtom, {
      ...currentState,
      isAuthenticated,
      user,
      tokens,
      error: null, // Clear any previous errors on successful auth
    });

    const pineSeedAuth = get(pineSeedAuthAtom);
    if (!pineSeedAuth) {
      throw new Error("Brand config not initialized");
    }
    const authHandler = AuthHandler.getInstance(pineSeedAuth);
    // Update persistent token storage
    if (tokens) {
      set(accessTokenAtom, tokens.accessToken);
      set(refreshTokenAtom, tokens.refreshToken || null);

      // Store auth state in secure storage via handler
      if (user) {
        await authHandler.storeAuthState(user, tokens);
      }

      // Update chat context with new authentication token
      try {
        const { updateChatAuthTokenAtom } = await import("./chat");
        set(updateChatAuthTokenAtom, tokens.accessToken);
      } catch (error) {
        console.warn("Failed to update chat auth token:", error);
      }
    } else {
      set(accessTokenAtom, null);
      set(refreshTokenAtom, null);
      await authHandler.clearStoredAuthState();

      // Clear chat context authentication token
      try {
        const { updateChatAuthTokenAtom } = await import("./chat");
        set(updateChatAuthTokenAtom, null);
      } catch (error) {
        console.warn("Failed to clear chat auth token:", error);
      }
    }
  },
);

export const clearAuthStateAtom = atom(null, async (get, set) => {
  set(authStateAtom, {
    isAuthenticated: false,
    user: null,
    tokens: null,
    isLoading: false,
    error: null,
    loginUrl: null,
    isInitialized: true, // Keep initialized state
  });

  // Clear persistent storage
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);

  const pineSeedAuth = get(pineSeedAuthAtom);
  if (pineSeedAuth) {
    const authHandler = AuthHandler.getInstance(pineSeedAuth);
    authHandler.clearStoredAuthState();
  }

  // Clear chat context authentication token
  try {
    const { updateChatAuthTokenAtom } = await import("./chat");
    set(updateChatAuthTokenAtom, null);
  } catch (error) {
    console.warn("Failed to clear chat auth token:", error);
  }
});

// ===== BUSINESS LOGIC INTEGRATION ACTIONS =====

export const getLoginUrlAtom = atom(null, async (get, set, state?: string) => {
  try {
    set(setLoadingAtom, true);
    set(setErrorAtom, null);

    const pineSeedAuth = get(pineSeedAuthAtom);
    if (!pineSeedAuth) {
      throw new Error("Brand config not initialized");
    }
    const authHandler = AuthHandler.getInstance(pineSeedAuth);
    const loginUrl = await authHandler.getLoginUrl(state);
    set(setLoginUrlAtom, loginUrl);

    return loginUrl;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to get login URL";
    set(setErrorAtom, errorMessage);
    throw error;
  } finally {
    set(setLoadingAtom, false);
  }
});

export const handleCallbackAtom = atom(
  null,
  async (get, set, code: string, state: string) => {
    try {
      set(setLoadingAtom, true);
      set(setErrorAtom, null);

      const pineSeedAuth = get(pineSeedAuthAtom);
      if (!pineSeedAuth) {
        throw new Error("Brand config not initialized");
      }

      // Get participantId for session merging
      const { participantIdAtom } = await import("./participant");
      const participantId = get(participantIdAtom);

      const authHandler = AuthHandler.getInstance(pineSeedAuth);
      const { user, tokens } = await authHandler.handleCallback(
        code,
        state,
        participantId?.deviceFingerprint,
      );
      set(setAuthenticatedAtom, true, user, tokens);

      return { user, tokens };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Authentication failed";
      set(setErrorAtom, errorMessage);
      throw error;
    } finally {
      set(setLoadingAtom, false);
    }
  },
);

export const getCurrentUserAtom = atom(null, async (get, set) => {
  try {
    set(setLoadingAtom, true);
    set(setErrorAtom, null);

    const pineSeedAuth = get(pineSeedAuthAtom);
    if (!pineSeedAuth) {
      throw new Error("Brand config not initialized");
    }
    const authHandler = AuthHandler.getInstance(pineSeedAuth);
    const user = await authHandler.getCurrentUser();

    // Update auth state with user info (keep existing tokens)
    const currentState = get(authStateAtom);
    set(setAuthenticatedAtom, true, user, currentState.tokens);

    return user;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to get user info";
    set(setErrorAtom, errorMessage);

    // If getting user fails, user is likely not authenticated
    set(clearAuthStateAtom);

    throw error;
  } finally {
    set(setLoadingAtom, false);
  }
});

export const refreshTokensAtom = atom(
  null,
  async (get, set, refreshToken?: string) => {
    try {
      set(setLoadingAtom, true);
      set(setErrorAtom, null);

      const currentState = get(authStateAtom);
      const tokenToUse =
        refreshToken ||
        currentState.tokens?.refreshToken ||
        get(refreshTokenAtom);

      if (!tokenToUse) {
        throw new Error("No refresh token available");
      }

      const pineSeedAuth = get(pineSeedAuthAtom);
      if (!pineSeedAuth) {
        throw new Error("Brand config not initialized");
      }
      const authHandler = AuthHandler.getInstance(pineSeedAuth);
      const newTokens = await authHandler.refreshToken(tokenToUse);

      // Update auth state with new tokens (keep existing user)
      set(setAuthenticatedAtom, true, currentState.user, newTokens);

      return newTokens;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to refresh tokens";
      set(setErrorAtom, errorMessage);

      // If refresh fails, clear auth state
      set(clearAuthStateAtom);

      throw error;
    } finally {
      set(setLoadingAtom, false);
    }
  },
);

export const logoutAtom = atom(null, async (get, set, token?: string) => {
  try {
    set(setLoadingAtom, true);
    set(setErrorAtom, null);

    const currentState = get(authStateAtom);
    const tokenToUse =
      token || currentState.tokens?.accessToken || get(accessTokenAtom);

    const pineSeedAuth = get(pineSeedAuthAtom);
    if (pineSeedAuth) {
      const authHandler = AuthHandler.getInstance(pineSeedAuth);
      await authHandler.logout(tokenToUse || undefined);
    }

    // Clear auth state
    set(clearAuthStateAtom);
  } catch (error) {
    // Even if logout API fails, clear local state
    set(clearAuthStateAtom);

    const errorMessage =
      error instanceof Error ? error.message : "Logout failed";
    set(setErrorAtom, errorMessage);
  } finally {
    set(setLoadingAtom, false);
  }
});

export const checkAuthStatusAtom = atom(null, async (get, set) => {
  try {
    set(setLoadingAtom, true);
    set(setErrorAtom, null);

    const pineSeedAuth = get(pineSeedAuthAtom);
    if (!pineSeedAuth) {
      throw new Error("Brand config not initialized");
    }
    const authHandler = AuthHandler.getInstance(pineSeedAuth);
    const result = await authHandler.checkAuthStatus();

    if (result.isAuthenticated && result.user) {
      // Auth handler already calls callbacks, but ensure state is updated
      const currentState = get(authStateAtom);
      if (!currentState.isAuthenticated) {
        // State not yet updated, get tokens from handler
        const storedAuth = await authHandler.loadStoredAuthState();
        if (storedAuth) {
          set(setAuthenticatedAtom, true, result.user, storedAuth.tokens);
        }
      }
      return { isAuthenticated: true, user: result.user };
    } else {
      set(clearAuthStateAtom);
      return { isAuthenticated: false, user: null };
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to check auth status";
    set(setErrorAtom, errorMessage);
    set(clearAuthStateAtom);
    return { isAuthenticated: false, user: null };
  } finally {
    set(setLoadingAtom, false);

    // Mark as initialized regardless of outcome
    const currentState = get(authStateAtom);
    set(authStateAtom, {
      ...currentState,
      isInitialized: true,
    });
  }
});

// ===== AUTH HANDLER INITIALIZATION =====

// OAuth callback setup atom
export const setupOAuthCallbacksAtom = atom(null, (get) => {
  const pineSeedAuth = get(pineSeedAuthAtom);
  if (!pineSeedAuth) {
    throw new Error("Brand config not initialized");
  }
  const authHandler = AuthHandler.getInstance(pineSeedAuth);
  authHandler.setupOAuthCallbackListeners();
});

// Popup login atom - follows architectural pattern of delegating to handler
export const loginWithPopupAtom = atom(
  null,
  async (get, set, state?: string) => {
    try {
      set(setLoadingAtom, true);
      set(setErrorAtom, null);

      const pineSeedAuth = get(pineSeedAuthAtom);
      if (!pineSeedAuth) {
        throw new Error("Brand config not initialized");
      }
      const authHandler = AuthHandler.getInstance(pineSeedAuth);

      const result = await authHandler.loginWithPopup(state);

      // Update auth state explicitly (same pattern as handleCallbackAtom)
      // This ensures chat system and oRPC bridge get notified of the new auth state
      set(setAuthenticatedAtom, true, result.user, result.tokens);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Popup login failed";
      set(setErrorAtom, errorMessage);
      throw error;
    } finally {
      set(setLoadingAtom, false);
    }
  },
);

export const initializeAuthHandlerAtom = atom(
  null,
  async (get, set, pineSeedAuth: PSAuthConfig) => {
    // If pineSeedAuth is provided, store it in the atom
    if (pineSeedAuth) {
      set(pineSeedAuthAtom, pineSeedAuth);
    }

    // Get pineSeedAuth from atom
    const storedBrandConfig = get(pineSeedAuthAtom);
    if (!storedBrandConfig) {
      throw new Error(
        "Brand config must be provided before initializing auth handler",
      );
    }

    // Validate required auth configuration
    const casdoorConfig = storedBrandConfig.casdoor;
    if (!casdoorConfig) {
      throw new Error("Casdoor configuration is missing from brand config");
    }

    // Get current auth config (may be empty initially)
    const config = get(authConfigAtom);

    // Use pineSeedAuth values as primary source, fallback to authConfig if needed
    const requiredFields = {
      serverUrl: casdoorConfig.serverUrl || config.casdoorEndpoint,
      clientId: casdoorConfig.clientId || config.clientId,
      organizationName:
        casdoorConfig.organizationName || config.organizationName,
      appName: casdoorConfig.appName || config.appName,
    };

    // Validate required fields are not blank
    for (const [field, value] of Object.entries(requiredFields)) {
      if (!value || value.trim() === "") {
        throw new Error(
          `Auth configuration field '${field}' cannot be blank. Please check your brand config.`,
        );
      }
    }

    // console.log(
    //   "Initializing auth handler with validated config:",
    //   requiredFields,
    // );

    // Update authConfig with values from pineSeedAuth
    await set(authConfigAtom, {
      casdoorEndpoint: requiredFields.serverUrl,
      clientId: requiredFields.clientId,
      organizationName: requiredFields.organizationName,
      appName: requiredFields.appName,
      redirectUri: casdoorConfig.redirectPath || "/auth/callback",
      scope: "openid profile email",
    });

    const authHandler = AuthHandler.getInstance(storedBrandConfig);

    // Initialize auth handler with configuration and callbacks
    await authHandler.initialize({
      casdoorConfig: {
        serverUrl: requiredFields.serverUrl,
        clientId: requiredFields.clientId,
        organizationName: requiredFields.organizationName,
        appName: requiredFields.appName,
        redirectPath: casdoorConfig.redirectPath || "/auth/callback",
      },
      callbacks: {
        onAuthStateChange: (
          isAuthenticated: boolean,
          user: CasdoorUser | null,
          tokens: AuthTokens | null,
        ) => {
          set(setAuthenticatedAtom, isAuthenticated, user, tokens);
        },

        onError: (error: string) => {
          set(setErrorAtom, error);
        },

        onLoading: (loading: boolean) => {
          set(setLoadingAtom, loading);
        },

        onLoginUrlReceived: (url: string) => {
          set(setLoginUrlAtom, url);
        },
      },
    });

    // Check auth status on initialization
    await set(checkAuthStatusAtom);
  },
);

// ===== LEGACY COMPATIBILITY ATOMS =====
// These atoms provide compatibility with existing components that use legacy names

export const clearAuthAtom = clearAuthStateAtom;
export const setAuthSuccessAtom = setAuthenticatedAtom;
export const setAuthErrorAtom = setErrorAtom;
export const setAuthLoadingAtom = setLoadingAtom;

export const updateUserAtom = atom(null, (get, set, user: CasdoorUser) => {
  const currentState = get(authStateAtom);
  set(authStateAtom, {
    ...currentState,
    user,
  });
});

// Internal atom for auth UI mode
const _authUIModeAtom = atom<string>("login");

// UI mode atom (for login screen state) - SECURE STORAGE
export const authUIModeAtom = atom(
  (get) => get(_authUIModeAtom),
  async (_get, set, newValue: string) => {
    await secureStorage.setItem(SecureStorageKeys.AUTH_UI_MODE, newValue);
    set(_authUIModeAtom, newValue);
  },
);

// Auth providers configuration
export const authProvidersAtom = atom((get) => {
  const pineSeedAuth = get(pineSeedAuthAtom);
  const providers = [];

  if (pineSeedAuth) {
    // Add OAuth providers if configured
    if (pineSeedAuth.casdoor?.providers?.oauth) {
      for (const provider of pineSeedAuth.casdoor.providers.oauth) {
        providers.push({
          id: provider,
          name: provider.charAt(0).toUpperCase() + provider.slice(1),
          enabled: true,
          type: "oauth" as const,
        });
      }
    }

    // Add other provider types
    if (pineSeedAuth.casdoor?.providers?.ldap) {
      providers.push({
        id: "ldap",
        name: "LDAP",
        enabled: true,
        type: "ldap" as const,
      });
    }

    if (pineSeedAuth.casdoor?.providers?.saml) {
      providers.push({
        id: "saml",
        name: "SAML",
        enabled: true,
        type: "saml" as const,
      });
    }

    if (pineSeedAuth.casdoor?.providers?.webauthn) {
      providers.push({
        id: "webauthn",
        name: "WebAuthn",
        enabled: true,
        type: "webauthn" as const,
      });
    }
  }

  // Fallback to default Casdoor provider if no providers configured
  if (providers.length === 0) {
    providers.push({
      id: "casdoor",
      name: "Casdoor",
      enabled: true,
      type: "oauth" as const,
    });
  }

  return providers;
});

// SSO configuration
export const ssoConfigAtom = atom((get) => {
  const config = get(authConfigAtom);
  const pineSeedAuth = get(pineSeedAuthAtom);
  return {
    enabled: pineSeedAuth?.casdoor?.sso?.enabled ?? true,
    autoSignin: pineSeedAuth?.casdoor?.sso?.autoSignin ?? true,
    silentSignin: pineSeedAuth?.casdoor?.sso?.silentSignin ?? true,
    popupSignin: pineSeedAuth?.casdoor?.sso?.popupSignin ?? true,
    endpoint: config.casdoorEndpoint,
    clientId: config.clientId,
  };
});

// Silent signin requirement
export const silentSigninRequiredAtom = atom((get) => {
  const pineSeedAuth = get(pineSeedAuthAtom);
  return pineSeedAuth?.casdoor?.sso?.silentSignin ?? false;
});

// Internal atom for PKCE verifier
const _pkceCodeVerifierAtom = atom<string | null>(null);

// PKCE code verifier storage - SECURE STORAGE
export const pkceCodeVerifierAtom = atom(
  (get) => get(_pkceCodeVerifierAtom),
  async (_get, set, newValue: string | null) => {
    if (newValue) {
      await secureStorage.setItem(SecureStorageKeys.PKCE_VERIFIER, newValue);
    } else {
      await secureStorage.removeItem(SecureStorageKeys.PKCE_VERIFIER);
    }
    set(_pkceCodeVerifierAtom, newValue);
  },
);
